import React, { useCallback, useEffect, useState } from 'react';
import { AfterPayBanner, AlertsList, EmptyProductsFallback } from '../../components';
import {
	BREADCRUMB,
	HELPBUTTON,
	PAGETITLE,
	PAYPALBANNER,
	PRODUCTTILES,
	PROMOMSG
} from '../../constants/rootElements';
import { useFeeAlertInjectionScript, useGoogleAnalyticsHandler } from "../../hooks";
import { gaPageLoad } from '../../util/googleAnalytics';
import { isAllSubCatalogProductsEmpty } from '../../util/isAllSubCatalogProductsEmpty';
import { Breadcrumb } from '../breadcrumb';
import { HelpButton } from '../help';
import { ProductBrowseTiles } from '../home/productBrowseTiles';
import { TitleBar } from '../productBrowseTitle';

export const RegaliaMultiGroupPage = ({ appData }) => {
	const [productGroups, setProductGroups] = useState({});
	const [breadcrumbData, setBreadcrupmData] = useState([]);
	const [promoMessages, setPromoMessages] = useState([]);
	const [feeAlertRequestData, setFeeAlertRequestData] = useState(null);
	const [googleAnalyticsRequestData, setGoogleAnalyticsRequestData] = useState(null);
    const [currencyCode, setCurrencyCode] = useState(null);

	useFeeAlertInjectionScript(feeAlertRequestData);
	const { selectItemTrackingGASY25, viewModalTrackingGASY25, addToCartTrackingGASY25 } = useGoogleAnalyticsHandler(googleAnalyticsRequestData);

	const RegaliaBreadcrumb = Boolean(
		appData?.pagecontentData?.find((data) => data.contentType === 'Navigation')
	);
	const RegaliaTitleBar = appData?.pagecontentData?.find(
		(data) => data.contentType === 'ProductBrowseTitle'
	);
	const ProductBrowseProducts = appData?.pagecontentData?.find(
		(data) => data.contentType === 'ProductBrowseProducts'
	);
	const ProductBrowsePromoMsg = Boolean(
		appData?.pagecontentData?.find(
			(data) => data.contentType === 'ProductBrowsePromoMsg'
		)
	);
	const PaypalBannerData = appData?.pagecontentData?.find(
		(data) => data.contentType === 'PaypalId'
	);

	const getBreadcrumb = useCallback(() => {
		BREADCRUMB.render(<Breadcrumb pbData={breadcrumbData} />);
	}, [breadcrumbData]);

	const getPromoMsg = useCallback(() => {
		PROMOMSG.render(<AlertsList messages={promoMessages} />);
	}, [promoMessages]);

	const getTitleBar = useCallback(() => {
		PAGETITLE.render(
			<TitleBar
				pageContent={RegaliaTitleBar}
				affiliationData={appData?.affiliationData}
			/>
		);
	}, []);
	
	const productTiles = useCallback(() => {
		//! Preparing Expected data Format For EmptyProductsFallback
		const isSubCatalogProductsEmpty = isAllSubCatalogProductsEmpty({
			subCatalogs: { 0: { productGroups } }
		})
		const subCatalogsPbData = productGroups && Object.values(productGroups).length
		? {homewardPathSegments: productGroups[0]?.homewardPathSegments || []}
		: {}

		PRODUCTTILES.render(
			isSubCatalogProductsEmpty
				? <EmptyProductsFallback
					productBrowseData={subCatalogsPbData}
				/>
				: <div>
					{Object.keys(productGroups || {}).map(productGroupKey => (
						<ProductBrowseTiles
							key={productGroupKey}
							pbData={{ "products": productGroups[productGroupKey].products || [] }}
							pageContent={{
								title: productGroups[productGroupKey].displayName
							}}
							productInfo={{ "prodDescDotCms": false, "performSort": false }}
							isSliderView={true}
							googleAnalytics={{ 
								selectItemTrackingGASY25, 
								viewModalTrackingGASY25, 
								addToCartTrackingGASY25,
								catalogOSR: googleAnalyticsRequestData?.catalogOsr,
								customerClassCode: appData.affiliationData?.customerClassCode
							}}
							sectionTitle={productGroups[productGroupKey].displayName}
						/>
					))}
				</div>
		);
		// eslint-disable-next-line
	}, [productGroups, selectItemTrackingGASY25]);

	const getHelpButton = useCallback(() => {
        HELPBUTTON.render(<HelpButton />);
  	},[])

      const paypalBanner = useCallback(() => {
        PAYPALBANNER.render(<AfterPayBanner currencyCode={currencyCode} />);
    },[PaypalBannerData, currencyCode])
    
    useEffect(()=>{
		gaPageLoad();
    },[])

	useEffect(() => {
		Object.keys(appData.productBrowseData).map((key) => {
			const mainCatalog = appData.productBrowseData[key]
			if ((key !== 'metaData') && (mainCatalog?.catalogOsr === 'col_fq') && (mainCatalog?.subCatalogs)) {
				const productGroups = Object.values(mainCatalog.subCatalogs)
					.flatMap(subCatalog => subCatalog.productGroups)
					.reduce((acc, subCatalogProductGroups) => 
						acc = { ...acc, ...subCatalogProductGroups }
					, {})
				const customerNumber = appData.productBrowseData.metaData.customerNumber;
				const catalog = appData.productBrowseData[key];
				setProductGroups(productGroups);
				setPromoMessages(appData.productBrowseData[key].promoMessages);
				setFeeAlertRequestData({
					containerSelector: '#promoMsg',
					customerId: customerNumber,
					catalogOsr: catalog.catalogOsr,
				});
				setGoogleAnalyticsRequestData({
					customerNumber,
					publishedCatalogId: catalog.publishedCatalogId,
					sourceCatalogType: catalog.sourceCatalogType,
					catalogOsr: catalog.catalogOsr,
					customerClassCode: appData.affiliationData?.customerClassCode
				});

				const browseProductGroups = appData.productBrowseData[key]?.productGroups;
				browseProductGroups && Object.keys(browseProductGroups).map(
						(catObj) => {
							const productGroup = browseProductGroups[catObj];
							setBreadcrupmData(productGroup);
							const _currencyCode = productGroup?.products?.[0]?.currencyCode;
							_currencyCode && setCurrencyCode(_currencyCode);
							return true;
						}
					);
			}
			return true;
		});
	}, [appData.productBrowseData]);

	return (
		<>
			{RegaliaTitleBar && Object.keys(RegaliaTitleBar)?.length > 0 && getTitleBar()}
			{ProductBrowsePromoMsg && promoMessages?.length > 0 && getPromoMsg()}
			{RegaliaBreadcrumb && getBreadcrumb()}
			{ProductBrowseProducts &&
				Object.keys(ProductBrowseProducts)?.length > 0 &&
				productTiles()}
			{
				getHelpButton()
			}
			{PaypalBannerData &&
				Object.keys(PaypalBannerData)?.length > 0 &&
				paypalBanner()}
		</>
	);
};
